import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    getRailPanelConfig, updateRailPanel, ApiCallError, type RailPanelConfigDto,
} from '@/api/client';

interface Props {
    getToken: () => Promise<string>;
}

/**
 * RIGHT-SIDE TOOLBAR admin (dock.RailPanel + dock.FacilityRailPanel, migration 172).
 *
 * ---- ⭐ WHAT PETE ASKED FOR -------------------------------------------------
 *
 * > Pete, 2026-09-08: *"Can you those cards (scanned papwerork, labor,
 * > communications and Driver Checkin) have an active/inactive flag in config
 * > screen? … The config of this might have to have its own config screen for
 * > something like RIght-Side ToolBar or something."*
 *
 * And, looking at the deployed rail: *"Scanned Paperwork is still Visible."*
 * That sentence is this screen, unbuilt.
 *
 * ---- ⭐⭐ A GRID, NOT A FACILITY PICKER, AND THE REASON IS THE QUESTION ------
 *
 * ⚠️ Every other per-facility screen here opens on ONE site. This one does not,
 * because the question an admin brings to it is comparative: *"which sites have
 * Labor switched off?"* ⛔ A picker answers that only by visiting every facility
 * in turn and remembering — which is how a site gets missed, and a missed site
 * looks exactly like a site that was deliberately left alone.
 *
 * ⭐ THE WHOLE TABLE IS FOUR COLUMNS WIDE AND ROUGHLY AS MANY ROWS AS THERE ARE
 * BUILDINGS. It fits.
 *
 * ---- ⛔⛔ "ON" AND "ON BY DEFAULT" ARE DIFFERENT AND THE SCREEN SAYS SO -----
 *
 * ⚠️ Migration 172 seeds NO per-facility rows: absence means ON. So a ticked box
 * can mean two things — nobody has ever touched this, or somebody switched it
 * off and back on. ⭐ Identical on screen, and the difference is exactly what an
 * admin needs when they are asking "did my change save?".
 *
 * ⛔ THAT AMBIGUITY IS THE KNOWN COST OF ABSENCE-MEANS-ON, named in the
 * migration header rather than glossed. This screen pays it back with
 * `isExplicit`: a set row is marked, a default one says "default".
 *
 * ---- ⚠️ DRIVER CHECK-INS CANNOT BE SWITCHED OFF ----------------------------
 *
 * ⛔ `CHECKIN` is `IsOptional = 0`. Its box is rendered — disabled and ticked —
 * rather than left out, because a blank cell in a grid reads as "not configured
 * yet" and invites somebody to go looking for the missing control. ⭐ A disabled
 * box with a reason attached answers the question where it is asked.
 *
 * ⚠️ The server refuses it too (`setRailPanel`). This is a courtesy, not the
 * enforcement — a disabled input is a suggestion.
 */
export default function RailPanelAdmin({ getToken }: Props) {
    const [rows, setRows] = useState<RailPanelConfigDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    /** ⭐ Keyed per CELL, so one slow save does not grey out the whole grid. */
    const [saving, setSaving] = useState<string | null>(null);

    /**
     * ---- ⭐⭐ "DID THAT SAVE?" NEEDS AN ANSWER, NOT AN INFERENCE ------------
     *
     * > Pete, 2026-09-09: *"I check/uncheck but not save, no message it was
     * > saved, etc."*
     *
     * ⛔ IT WAS SAVING ALL ALONG — Brighton's Scanned Paperwork was off and
     * persisted before he asked. ⚠️ **That is the complaint, not a defence of
     * the screen.** A write that lands with no acknowledgement is
     * indistinguishable from one that did not, so the honest reading of the
     * screen was "nothing happened", and the honest reading was wrong.
     *
     * ⭐ THE DIALOG'S OWN SUBTITLE SAYS *"Changes take effect immediately"* —
     * which is true, and is a promise the grid was not keeping visibly.
     *
     * ⚠️ IT CLEARS ITSELF. A "Saved" that stays forever stops meaning "just
     * now", which is the only thing it is for.
     */
    const [savedCell, setSavedCell] = useState<string | null>(null);
    const savedTimer = useRef<number | null>(null);

    /* ⛔ CLEARED ON UNMOUNT. The dialog closes the moment somebody clicks away,
       and a timer firing into a dead component is a React warning nobody can
       act on. */
    useEffect(() => () => {
        if (savedTimer.current) window.clearTimeout(savedTimer.current);
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const { rows: r } = await getRailPanelConfig(await getToken());
            setRows(r);
        } catch (err) {
            /**
             * ⛔⛔ ~~THE HELPFUL MESSAGE WAS THE `else` BRANCH~~ — which is to say
             * it was UNREACHABLE for the only case it was written for.
             *
             * ⚠️ MEASURED 2026-09-08. The server threw, the client got an
             * `ApiCallError`, the ternary took the FIRST branch, and the screen
             * showed the stock **"Internal server error."** The sentence naming
             * migration 172 sat in the branch that only fires when the network is
             * gone. ⭐ A message written for a case it cannot reach is worse than
             * no message at all, because writing it feels like handling the case.
             *
             * ⭐ THE SERVER'S TEXT NOW CARRIES THE REAL FAULT (see `adminError` in
             * railPanels.ts), so it leads. The hint follows as its own line below
             * rather than replacing it — see the render.
             */
            setError(err instanceof ApiCallError
                ? err.details.error
                : 'Could not reach the server.');
        } finally {
            setLoading(false);
        }
    }, [getToken]);

    useEffect(() => { void load(); }, [load]);

    /**
     * ⭐ THE PANEL VOCABULARY COMES FROM THE DATA, NOT A LIST IN THIS FILE.
     * §11.4 — a hardcoded column list disagrees with the rail the first time a
     * fifth card is added, and disagrees SILENTLY: the card renders and the
     * screen that configures it has no column for it.
     */
    const panels = useMemo(() => {
        const seen = new Map<string, { code: string; name: string; sortOrder: number; isOptional: boolean }>();
        for (const r of rows) {
            if (!seen.has(r.panelCode)) {
                seen.set(r.panelCode, {
                    code: r.panelCode, name: r.displayName,
                    sortOrder: r.sortOrder, isOptional: r.isOptional,
                });
            }
        }
        return [...seen.values()].sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code));
    }, [rows]);

    /** One entry per facility, in the order the server sent them (company, then code). */
    const facilities = useMemo(() => {
        const seen = new Map<string, {
            key: string; companyId: number; companyCode: string;
            facilityCode: string; facilityName: string | null;
            byPanel: Map<string, RailPanelConfigDto>;
        }>();
        for (const r of rows) {
            const key = `${r.companyId}|${r.facilityCode}`;
            let f = seen.get(key);
            if (!f) {
                f = {
                    key, companyId: r.companyId, companyCode: r.companyCode,
                    facilityCode: r.facilityCode, facilityName: r.facilityName,
                    byPanel: new Map(),
                };
                seen.set(key, f);
            }
            f.byPanel.set(r.panelCode, r);
        }
        return [...seen.values()];
    }, [rows]);

    const toggle = useCallback(async (row: RailPanelConfigDto) => {
        const cell = `${row.companyId}|${row.facilityCode}|${row.panelCode}`;
        setSaving(cell);
        setError(null);
        try {
            const { rows: fresh } = await updateRailPanel(await getToken(), {
                companyId:    row.companyId,
                facilityCode: row.facilityCode,
                panelCode:    row.panelCode,
                isActive:     !row.isActive,
            });
            // ⭐ THE SERVER'S GRID REPLACES OURS WHOLESALE. No optimistic patching:
            //   a checkbox that shows what we hoped happened is the one thing this
            //   screen must not do.
            setRows(fresh);
            /* ⭐ AFTER the server's grid replaces ours, never before — the point
               of the word is that the DATABASE agreed, not that we asked. */
            setSavedCell(cell);
            if (savedTimer.current) window.clearTimeout(savedTimer.current);
            savedTimer.current = window.setTimeout(() => setSavedCell(null), 2500);
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not save that change.');
        } finally {
            setSaving(null);
        }
    }, [getToken]);

    return (
        <div className="space-y-3 text-sm">
            <div>
                <h3 className="text-base font-medium">Right-Side Toolbar</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                    Which cards appear down the right of the schedule, per facility.
                    A card with no setting is shown — this table holds only what has been changed.
                </p>
            </div>

            {error && (
                <div className="space-y-1 rounded border border-destructive/40 bg-destructive/10 p-2 text-xs">
                    <div>{error}</div>
                    {/* ⚠️ A HINT, NOT A DIAGNOSIS. It sits BESIDE the server's message
                        rather than instead of it, because the two most common causes
                        look identical from here and guessing between them is what cost
                        an evening. */}
                    <div className="text-muted-foreground">
                        If the rail has never been set up here, check that migration 172 has been
                        run and that dock.RailPanel has its four rows. db\diag-172.sql answers both.
                    </div>
                </div>
            )}

            {loading ? (
                <div className="text-xs text-muted-foreground">Loading…</div>
            ) : facilities.length === 0 ? (
                /**
                 * ⚠️ THIS IS NOT "EVERYTHING IS OFF". An empty grid here means no
                 * active facilities came back at all, and saying so is the difference
                 * between a five-minute fix and a bug report about missing cards.
                 */
                <div className="rounded border bg-muted/30 p-3 text-xs text-muted-foreground">
                    No active facilities came back, so there is nothing to configure.
                    That is a facilities problem rather than a rail one — check that
                    migration 172 has been run and that facilities are marked active.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-xs">
                        <thead>
                            <tr className="border-b text-left">
                                <th className="py-1.5 pr-3 font-medium">Facility</th>
                                {panels.map((p) => (
                                    <th key={p.code} className="px-2 py-1.5 text-center font-medium">
                                        {p.name}
                                        {!p.isOptional && (
                                            <div className="text-[10px] font-normal text-muted-foreground">
                                                always on
                                            </div>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {facilities.map((f, i) => {
                                // ⭐ A company heading only when it CHANGES. Most
                                //   installs are one company and a repeated banner
                                //   would be noise; a second company has to be
                                //   unmistakable, because this table is the one place
                                //   two companies' facilities sit next to each other.
                                const prev = facilities[i - 1];
                                const newCompany = !prev || prev.companyId !== f.companyId;
                                return (
                                    <tr key={f.key} className="border-b last:border-b-0">
                                        <td className="py-1.5 pr-3 align-middle">
                                            {newCompany && facilities.some(x => x.companyId !== f.companyId) && (
                                                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                                    {f.companyCode}
                                                </div>
                                            )}
                                            <span className="font-medium">{f.facilityCode}</span>
                                            {f.facilityName && (
                                                <span className="text-muted-foreground"> · {f.facilityName}</span>
                                            )}
                                        </td>
                                        {panels.map((p) => {
                                            const row = f.byPanel.get(p.code);
                                            const cell = `${f.companyId}|${f.facilityCode}|${p.code}`;
                                            if (!row) {
                                                // ⚠️ Should be impossible — the server
                                                //    CROSS JOINs — so say so rather than
                                                //    rendering a blank that reads as "off".
                                                return (
                                                    <td key={p.code} className="px-2 py-1.5 text-center text-muted-foreground">
                                                        ?
                                                    </td>
                                                );
                                            }
                                            return (
                                                <td key={p.code} className="px-2 py-1.5 text-center align-middle">
                                                    <input
                                                        type="checkbox"
                                                        checked={row.isActive}
                                                        disabled={!row.isOptional || saving !== null}
                                                        onChange={() => void toggle(row)}
                                                        aria-label={`${p.name} at ${f.facilityCode}`}
                                                        title={!row.isOptional
                                                            ? 'Driver Check-ins cannot be switched off — the rail exists to show it.'
                                                            : row.isActive
                                                                ? (row.isExplicit
                                                                    ? 'Visible — switched on here'
                                                                    : 'Visible — the shipped default; nobody has changed this')
                                                                : 'Hidden at this facility — switched off here'}
                                                    />
                                                    {/**
                                                      * ---- ⭐⭐ IT SAYS WHAT YOU SEE, NOT WHERE IT CAME FROM ----
                                                      *
                                                      * > Pete, 2026-09-09: *"Default vs Visible — I think visible
                                                      * > is better."*
                                                      *
                                                      * ⚠️ ~~`default` / `set`~~ answered a question the admin was
                                                      * not asking. It described the ROW'S PROVENANCE — whether
                                                      * anybody had ever touched this cell — when the thing they
                                                      * came to find out is whether the card SHOWS.
                                                      *
                                                      * ⭐ PROVENANCE IS NOT DISCARDED; it moved to the tooltip,
                                                      * where it answers a rarer question without competing with the
                                                      * common one. The migration header's point still stands — "on"
                                                      * and "on by default" ARE different — it just is not the
                                                      * headline.
                                                      *
                                                      * ⛔ AND "Saved" OUTRANKS BOTH FOR A MOMENT. Immediately after
                                                      * a write, the useful sentence is not what the state is; it is
                                                      * that the state CHANGED and the server agreed.
                                                      */}
                                                    <div className="mt-0.5 text-[10px] leading-none">
                                                        {saving === cell ? (
                                                            <span className="text-muted-foreground">saving…</span>
                                                        ) : savedCell === cell ? (
                                                            <span className="font-medium text-emerald-700">Saved</span>
                                                        ) : !row.isOptional ? (
                                                            ''
                                                        ) : row.isActive ? (
                                                            <span className="text-muted-foreground">Visible</span>
                                                        ) : (
                                                            /* ⚠️ HIDDEN IS THE EXCEPTION AND READS LIKE ONE. It is
                                                               the state somebody chose, on a screen where every
                                                               other cell is the default — so it earns the colour. */
                                                            <span className="font-medium text-amber-700">Hidden</span>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="text-[11px] text-muted-foreground">
                {/**
                  * ⚠️ THE PER-USER HALF IS NAMED HERE ON PURPOSE. Somebody WILL come
                  * to this screen looking for "why is Labor folded up" — that is a
                  * scheduler's own chevron, stored in their browser, and nothing on
                  * this page can change it. Saying so is cheaper than the support
                  * conversation.
                  */}
                Switching a card off hides it for everyone at that facility. Folding a card
                shut with its arrow is personal to one browser and is not set here.
            </p>
        </div>
    );
}
